import { useState, useEffect, useCallback } from 'react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, loginRequest, initializeMsal } from '../auth/msalConfig';
import { isDemoMode } from '../utils/demoMode';
import { fakeUpn, fakePersonName, fakeMspCode, fakeMspName } from '../utils/demoAnonymizer';
import { classifyMeStatus, resolveAuthGate } from '../utils/authGate';
import { fetchWithRetry } from '../utils/fetchWithTimeout';

// /api/me is the portal's FIRST call and gates every screen, so it gets a hard
// timeout and a bounded retry (v1.184.2). On 2026-09-18/19 the API host's
// managed-identity sidecar stalled on fresh workers and /api/me calls that landed
// on one waited 62–394 s; the raw fetch had no timeout, so the user saw a bare
// spinner for minutes and read it as "down". 20 s is well above a healthy cold
// start (~1–5 s measured) and well below the platform's own ~230 s request cap;
// three attempts because a retry is a NEW request that may land on a healthy
// instance. After the last attempt the existing 'unreachable' gate renders the
// honest "can't reach the service" screen. Tests: scripts/fetchWithTimeout.test.mjs
const ME_TIMEOUT_MS = 20000;
const ME_ATTEMPTS = 3;

// fetchMeFromApi
//   Calls GET /api/me with the freshly-acquired bearer token and reports WHY it
//   failed rather than collapsing every failure to null (which is what made an
//   API outage render as "Access Denied" — see
//   Issues/2026-08-19_Api-Outage-Renders-As-Access-Denied and utils/authGate.js).
//   Returns { status, me } where status is 'ok' | 'unauthorized' | 'unreachable'.
//   Implemented with fetchWithRetry (a dependency-free util) instead of importing
//   from client.js so we avoid creating a useAuth.js <-> client.js import cycle
//   (client.js already imports getApiToken from this file).
//   `onProgress` is told before each retry so the loading screen can say
//   "slow to respond — retrying" instead of spinning silently.
async function fetchMeFromApi(onProgress) {
  const token = await getApiToken();
  if (!token) {
    console.warn('[Auth] /api/me: no bearer token (could not ask)');
    return { status: classifyMeStatus({ noToken: true }), me: null };
  }
  const base = import.meta.env.VITE_API_BASE_URL || '';
  let resp;
  try {
    resp = await fetchWithRetry(`${base}/api/me`, {
      headers: { Authorization: `Bearer ${token}` },
    }, {
      timeoutMs: ME_TIMEOUT_MS,
      attempts: ME_ATTEMPTS,
      onRetry: (p) => {
        console.warn(`[Auth] /api/me attempt ${p.attempt}/${p.attempts} failed (${p.reason}); retrying (attempt ${p.nextAttempt})`, p.error);
        if (typeof onProgress === 'function') onProgress(p);
      },
    });
  } catch (err) {
    // Network / CORS / timeout — a fetch rejection after every attempt, not an HTTP answer.
    console.warn(`[Auth] /api/me failed after ${err?.attempts ?? ME_ATTEMPTS} attempt(s):`, err);
    return { status: classifyMeStatus({ networkError: true }), me: null };
  }
  if (resp.status === 200) {
    try {
      const me = await resp.json();
      return { status: 'ok', me };
    } catch (err) {
      console.warn('[Auth] /api/me returned 200 but body did not parse:', err);
      return { status: classifyMeStatus({ httpStatus: 200, parseError: true }), me: null };
    }
  }
  const status = classifyMeStatus({ httpStatus: resp.status });
  console.warn(`[Auth] /api/me returned HTTP ${resp.status} -> ${status}`);
  return { status, me: null };
}

// setUserFromAccount
//   Builds the user object surfaced by useAuth(). Authoritative MSP fields come
//   from /api/me. There is deliberately NO token-claims fallback: when /api/me
//   is unreachable the app renders an honest "can't reach the service" retry
//   screen (App.jsx keys on authGate === 'unreachable'), because the API serves
//   every page's data anyway, so a degraded shell is useless — and the old
//   fallback denied exactly the non-admin MSP population while waving admins in.
async function setUserFromAccount(account, onProgress) {
  const isDev = !import.meta.env.PROD;

  const { status: meStatus, me } = await fetchMeFromApi(onProgress);

  const isGlobalAdmin = isDev || !!(me?.isGlobalAdmin);
  let mspCodes          = me?.mspCodes      ?? [];
  let adminMspCodes     = me?.adminMspCodes ?? [];
  const allowedTenantIds = me?.allowedTenantIds ?? null; // null = global / unbounded
  let mspCatalog        = me?.mspCatalog    ?? [];
  const isMspAdmin      = adminMspCodes.length > 0;

  // Demo Mode: /api/me is fetched with raw `fetch` (deliberately, to avoid an
  // import cycle with client.js), so it bypasses the anonymizer in request().
  // Fake the identity + MSP fields here instead, using the SAME mappers the
  // response anonymizer uses so catalog ↔ row ↔ request-key joins still line up
  // (fakeMspCode also registers the reverse mapping, so MSP-keyed reads —
  // PSA options/resources, suppression-summary filters — restore the real code
  // on the wire). Authorization fields (isGlobalAdmin / isAuthorized /
  // allowedTenantIds) stay REAL: they're internal scoping and never rendered.
  const demo = isDemoMode();
  if (demo) {
    // Map the catalog first so each fake code is paired with its fake name.
    mspCatalog = mspCatalog.map((row) => {
      const link = { mspCode: row?.code, mspName: row?.displayName };
      return {
        ...row,
        code: fakeMspCode(row?.code, link),
        displayName: fakeMspName(row?.displayName, link),
      };
    });
    mspCodes = mspCodes.map((c) => fakeMspCode(c));
    adminMspCodes = adminMspCodes.map((c) => fakeMspCode(c));
  }

  // authGate: which screen this authenticated user sees — 'app' | 'denied' |
  //   'unreachable'. Access Denied is ONLY 'denied' (we asked and the user is
  //   genuinely not entitled). 'unreachable' means /api/me could not be reached;
  //   App.jsx shows a retry screen, never Access Denied. See utils/authGate.js.
  //   mspCodes here is the pre-demo-anonymization membership from /api/me
  //   (fakeMspCode does not change the count), so the gate is computed on the
  //   real entitlement.
  const authGate = resolveAuthGate({
    isDev,
    meStatus,
    isGlobalAdmin: !!(me?.isGlobalAdmin),
    mspCodesCount: (me?.mspCodes ?? []).length,
  });
  const isAuthorized = authGate === 'app';

  const realUserDetails = account.username;
  const realDisplayName = me?.displayName || account.name || account.username;
  const realUpn = me?.userPrincipalName || account.username;

  return {
    userId: account.localAccountId,
    userDetails: demo ? fakeUpn(realUserDetails) : realUserDetails,
    displayName: demo ? fakePersonName(realDisplayName) : realDisplayName,
    upn: demo ? fakeUpn(realUpn) : realUpn,

    // Authorization tier flags (preferred for new code).
    isGlobalAdmin,
    isMspAdmin,
    isAuthorized,
    // Which screen to render: 'app' | 'denied' | 'unreachable'. isAuthorized is
    // (authGate === 'app'); authGate additionally tells App.jsx to show the
    // "can't reach the service" retry screen instead of a false Access Denied.
    authGate,

    // MSP membership / scope (driven by /api/me).
    mspCodes,
    adminMspCodes,
    allowedTenantIds,
    mspCatalog,
    overageFailClosed: !!(me?.overageFailClosed),

    // Back-compat: isAdmin previously meant "may perform admin-only actions."
    // Map it to global-admin for now; MSP-admin gates use isMspAdmin explicitly.
    isAdmin: isGlobalAdmin,
  };
}

/**
 * Authentication hook using MSAL.js for Azure AD.
 * Uses redirect-based flow: automatically redirects to AAD on first visit,
 * then processes the redirect response to establish a session.
 */
export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // { attempt, attempts, nextAttempt, reason } while /api/me is being retried;
  // null otherwise. App.jsx renders it under the loading spinner so a slow API
  // reads as "slow, retrying" rather than "down" (v1.184.2).
  const [authProgress, setAuthProgress] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      await initializeMsal();

      // 1. Check for cached account (set by initializeMsal from redirect response or cache)
      const account = msalInstance.getActiveAccount();
      if (account) {
        sessionStorage.removeItem('msal_redirect_attempted');
        const resolved = await setUserFromAccount(account, (p) => { if (!cancelled) setAuthProgress(p); });
        if (!cancelled) {
          setAuthProgress(null);
          setUser(resolved);
          setLoading(false);
        }
        return;
      }

      // 2. No account — automatically redirect to AAD (once).
      //    Skip in dev mode so local testing works with mock data.
      if (!import.meta.env.DEV && !sessionStorage.getItem('msal_redirect_attempted')) {
        sessionStorage.setItem('msal_redirect_attempted', 'true');
        try {
          await msalInstance.loginRedirect(loginRequest);
          return; // page navigates away
        } catch {
          // redirect failed — fall through to show login page
        }
      }

      // 3. Redirect already attempted or failed — show login page
      if (!cancelled) setLoading(false);
    }

    init();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async () => {
    try {
      await initializeMsal();
      sessionStorage.removeItem('msal_redirect_attempted');
      await msalInstance.loginRedirect(loginRequest);
    } catch (err) {
      console.error('[Auth] Login redirect failed:', err);
    }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem('msal_redirect_attempted');
    msalInstance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }, []);

  const isAuthenticated = !!user;

  return { user, loading, isAuthenticated, login, logout, authProgress };
}

/**
 * Acquires an access token for the Function App API.
 * Returns the token string, or null if not authenticated.
 */
export async function getApiToken() {
  try {
    await initializeMsal();
  } catch (err) {
    console.error('[Auth] MSAL init failed:', err);
    return null;
  }

  const account = msalInstance.getActiveAccount();
  if (!account) {
    console.warn('[Auth] No active account — API call will be unauthenticated');
    return null;
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
    });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // The cached access token is gone / expired and the refresh token
      // couldn't renew it silently. Before dropping to a full-page redirect
      // (which bounces the user back to redirectUri = origin = home, losing
      // the page they were on), try an iframe-based silent SSO — this uses
      // the existing AAD session cookie and avoids any visible navigation.
      try {
        const silent = await msalInstance.ssoSilent({
          ...loginRequest,
          loginHint: account.username,
        });
        msalInstance.setActiveAccount(silent.account);
        return silent.accessToken;
      } catch (ssoErr) {
        // ssoSilent also failed — genuine interaction required. Redirect,
        // but tell MSAL to return the user to the page they started on
        // instead of the default redirectUri (/).
        console.warn('[Auth] Silent SSO failed, redirecting to login...');
        try {
          await msalInstance.acquireTokenRedirect({
            ...loginRequest,
            redirectStartPage: window.location.href,
          });
        } catch (redirectErr) {
          console.error('[Auth] Redirect for token acquisition failed:', redirectErr);
        }
        return null;
      }
    }
    console.error('[Auth] Silent token acquisition failed:', err);
    return null;
  }
}

/**
 * Forces a full re-login redirect. Call this when the API returns 401.
 * Preserves the current page URL so the user returns where they started.
 */
export async function forceRelogin() {
  try {
    await initializeMsal();
    sessionStorage.removeItem('msal_redirect_attempted');
    await msalInstance.loginRedirect({
      ...loginRequest,
      redirectStartPage: window.location.href,
    });
  } catch (err) {
    console.error('[Auth] Force re-login failed:', err);
  }
}

/**
 * Non-destructive session probe used by the connection-error banner to decide,
 * after a request fails at the network level, whether the sign-in is actually
 * gone or the connection merely dropped. Unlike getApiToken(), this NEVER
 * redirects — it only reads MSAL's silent/cached state — so it's safe to call
 * purely to choose which message to show.
 *
 * Returns:
 *   'valid'   — a token is obtainable silently (cache, refresh token, or the AAD
 *               SSO cookie via ssoSilent); no sign-in needed, just retry/reload.
 *   'expired' — interaction is genuinely required (no account, or both the
 *               refresh token and SSO cookie are exhausted); the user must sign in.
 *   'unknown' — the probe itself couldn't complete (e.g. AAD unreachable, or a
 *               non-interaction ssoSilent failure such as blocked 3rd-party
 *               cookies); can't distinguish, so treat cautiously (offer both).
 */
export async function checkSessionValid() {
  try {
    await initializeMsal();
  } catch {
    return 'unknown';
  }

  const account = msalInstance.getActiveAccount();
  if (!account) return 'expired';

  try {
    await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    return 'valid';
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Cached/refresh token can't renew silently — try the AAD session cookie
      // (iframe SSO) before concluding the session is truly gone. This mirrors
      // getApiToken()'s recovery path, minus the redirect, so 'expired' means
      // "even SSO can't recover" — i.e. sign-in is actually required.
      try {
        const silent = await msalInstance.ssoSilent({ ...loginRequest, loginHint: account.username });
        msalInstance.setActiveAccount(silent.account);
        return 'valid';
      } catch (ssoErr) {
        if (ssoErr instanceof InteractionRequiredAuthError) return 'expired';
        return 'unknown'; // ssoSilent failed for a non-interaction reason (network / blocked cookies)
      }
    }
    // Non-interaction failure from acquireTokenSilent (often network/timeout) — inconclusive.
    return 'unknown';
  }
}
